import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaArrowUp, FaArrowDown, FaTicketAlt, FaRupeeSign, FaChartLine,
  FaCalendarCheck, FaUsers, FaFileDownload, FaCommentDots,
  FaFlask, FaBullhorn,
} from "react-icons/fa";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { fetchInquiries } from '../../features/getInTouchSlice';
import { Link } from "react-router-dom";

/* ─── Design tokens ──────────────────────────────────────────── */
const P = "#F04DA0";
const V = "#8B2FC9";
const D = "#1a0040";
const G = `linear-gradient(135deg, ${P}, ${V})`;
const BG = "#f4eefa";

/* ─── Icon + color map (label → static UI config) ───────────── */
const STAT_CONFIG = {
  "Registered Users": { icon: <FaUsers size={18} />, c: P, bg: "#fff0f8", border: "#ffd0ea" },
  "Total Revenue": { icon: <FaRupeeSign size={18} />, c: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  "Tickets Sold": { icon: <FaTicketAlt size={18} />, c: V, bg: "#faf5ff", border: "#e9d5ff" },
  "Total Leads": { icon: <FaBullhorn size={18} />, c: "#ea580c", bg: "#fff7ed", border: "#fed7aa" },
  "PDF Downloads": { icon: <FaFileDownload size={18} />, c: "#0284c7", bg: "#f0f9ff", border: "#bae6fd" },
  "Abstracts": { icon: <FaFlask size={18} />, c: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
  "Feedbacks": { icon: <FaCommentDots size={18} />, c: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
  "Events Held": { icon: <FaCalendarCheck size={18} />, c: "#b45309", bg: "#fffbeb", border: "#fde68a" },
};


const PDFS = [
  { name: "Dr. Nisha Tiwari", email: "nisha.tiwari@gmail.com", file: "Day 1 Schedule.pdf", time: "11:05 AM" },
  { name: "Dr. Aditya Sinha", email: "aditya.sinha@yahoo.com", file: "Full Program.pdf", time: "08:50 AM" },
  { name: "Dr. Swati Bose", email: "swati.bose@gmail.com", file: "Workshop Guide.pdf", time: "Yesterday" },
  { name: "Dr. Tarun Malhotra", email: "tarun.m@hotmail.com", file: "Day 2 Schedule.pdf", time: "Yesterday" },
  { name: "Dr. Kavya Iyer", email: "kavya.iyer@gmail.com", file: "Full Program.pdf", time: "12 Jul" },
];
const FEEDBACKS = [
  { name: "Dr. Priya Sharma", r: 5, comment: "Absolutely world-class sessions. The regenerative trichology track was outstanding.", date: "13 Jul" },
  { name: "Dr. Rohan Mehta", r: 4, comment: "Great workshops, very hands-on. Would love even more time for Q&A with faculty.", date: "13 Jul" },
  { name: "Dr. Anjali Nair", r: 5, comment: "Best conference I've attended. The laser sessions were practically very useful.", date: "14 Jul" },
  { name: "Dr. Siddharth Joshi", r: 4, comment: "Excellent organisation. The networking sessions were a real highlight.", date: "14 Jul" },
];



/* ─── Status map ─────────────────────────────────────────────── */
const ST = {
  Confirmed: { bg: "#f0fdf4", c: "#16a34a", b: "#bbf7d0" },
  Pending: { bg: "#fffbeb", c: "#b45309", b: "#fde68a" },
  Waitlisted: { bg: "#fff7ed", c: "#ea580c", b: "#fed7aa" },
  Accepted: { bg: "#f0fdf4", c: "#16a34a", b: "#bbf7d0" },
  "Under Review": { bg: "#f0f9ff", c: "#0284c7", b: "#bae6fd" },
  Rejected: { bg: "#fef2f2", c: "#dc2626", b: "#fecaca" },
};

/* ─── Micro components ───────────────────────────────────────── */
const Badge = ({ s }) => {
  const t = ST[s] || ST.Pending;
  return (
    <span style={{
      background: t.bg, color: t.c, border: `1px solid ${t.b}`, fontSize: 11, fontWeight: 700,
      letterSpacing: "0.04em", padding: "3px 10px", borderRadius: 6, textTransform: "uppercase", whiteSpace: "nowrap"
    }}>
      {s}
    </span>
  );
};

const Stars = ({ n }) => (
  <span style={{ display: "flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map(i => (
      <svg key={i} width="11" height="11" viewBox="0 0 24 24" fill={i <= n ? P : "#e2e8f0"} stroke="none">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </span>
);

const Av = ({ name, size = 36 }) => {
  const initials = name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const colors = [[P, V], ["#0284c7", "#0f766e"], ["#7c3aed", "#ea580c"], ["#16a34a", "#0284c7"]];
  const idx = name.charCodeAt(0) % colors.length;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(135deg,${colors[idx][0]},${colors[idx][1]})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", fontSize: size * 0.33, fontWeight: 800, letterSpacing: "0.02em"
    }}>
      {initials}
    </div>
  );
};

/* ─── KPI Skeleton loader ────────────────────────────────────── */
const KpiSkeleton = () => (
  <div className="kpi" style={{ "--kc": "#e0d0f0", "--kb": "#f5f0fa", "--kbo": "#ede5f5" }}>
    <div className="kpi-top">
      <div style={{ width: 42, height: 42, borderRadius: 11, background: "#f0e8f8" }} />
      <div style={{ width: 48, height: 20, borderRadius: 6, background: "#f0e8f8" }} />
    </div>
    <div style={{ width: "60%", height: 28, borderRadius: 6, background: "#f0e8f8", marginBottom: 8 }} />
    <div style={{ width: "80%", height: 12, borderRadius: 4, background: "#f5f0fa" }} />
  </div>
);

/* ─── Countdown ──────────────────────────────────────────────── */
function useCountdown(to) {
  const [t, setT] = useState({ d: "00", h: "00", m: "00", s: "00" });
  useEffect(() => {
    const tick = () => {
      const diff = new Date(to) - new Date();
      if (diff <= 0) return;
      setT({
        d: String(Math.floor(diff / 86400000)).padStart(2, "0"),
        h: String(Math.floor((diff / 3600000) % 24)).padStart(2, "0"),
        m: String(Math.floor((diff / 60000) % 60)).padStart(2, "0"),
        s: String(Math.floor((diff / 1000) % 60)).padStart(2, "0"),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [to]);
  return t;
}

/* ─── Section wrapper ────────────────────────────────────────── */
const Section = ({ label, icon, iconColor, action, children,show=true }) => (
  <div className="sec">
    <div className="sec-head">
      <div className="sec-head-left">
        <span className="sec-icon" style={{ background: `${iconColor}12`, color: iconColor, borderColor: `${iconColor}22` }}>
          {icon}
        </span>
        <span className="sec-label">{label}</span>
      </div>
      {(action && show) && (
        <Link to={action} className="sec-action">
          View all{" "}
          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      )}
    </div>
    {children}
  </div>
);

/* ════════════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const [countdownEvent, setCountdownEvent] = useState(null);
  const cd = useCountdown(
    countdownEvent?.countdownTo || new Date().toISOString()
  );
  const date = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  /* ── STATS state ── */
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [todayRegs, setTodayRegs] = useState(null);
  const [events, setEvents] = useState([]);
  const [regData, setRegData] = useState([]);
  const [revData, setRevData] = useState([]);
  const [users, setUsers] = useState([]);
  const [abstracts, setAbstracts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const dispatch = useDispatch();
  const { inquiries, loading } = useSelector((state) => state.getInTouch);
  const sortedData = [...(inquiries || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const formatDate = (dateString) => {
  const date = new Date(dateString);

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};
  useEffect(() => {
    dispatch(fetchInquiries());
  }, [dispatch]);
  /* ── Fetch STATS from API ── */
  useEffect(() => {

    fetchStats();
    fetchtoDaysRegistration()
    fetchUpcomingEvents()
    fetchCountdownEvent();
    fetchRegGrowth()
    fetchRevenue()
    fetchUsers()
    fetchAbstracts()
    fetchFeedbacks()
  }, []);
 
  
  const fetchFeedbacks = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/Feedback/feedback`);
      const responseData = response.data?.data ?? response.data ?? [];
      const list = Array.isArray(responseData) ? responseData : [];

      setFeedbacks(list);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

 
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/dashboard/stats`
      );
      if (data.success && data.stats) {
        // Merge API data (val, delta, up) with static UI config (icon, colors)
        const merged = data.stats.map((s) => ({
          ...s,
          ...(STAT_CONFIG[s.label] || {}),
        }));
        setStats(merged);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
      setStatsError("Stats load Error");
    } finally {
      setStatsLoading(false);
    }
  };
  const fetchtoDaysRegistration = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/dashboard/today-registrations`
      );
      if (data.success && data.data) {
        setTodayRegs(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    }
  };
  const fetchUpcomingEvents = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/dashboard/upcoming-events`
      );
      if (data.success && data.data) {
        setEvents(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    }
  };
  const fetchCountdownEvent = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/dashboard/countdown-event`
      );

      if (data.success && data.data) {
        setCountdownEvent(data.data);
      }
    } catch (err) {
      console.error("Countdown fetch error:", err);
    }
  };


  const fetchRegGrowth = async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/dashboard/registration-growth`
    );
    if (data.success) setRegData(data.data);
  };


  const fetchRevenue = async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/dashboard/revenue-by-month`
    );
    if (data.success) setRevData(data.data);
  };

  const fetchUsers = async () => {
    try {
      let statusValue = "";
      let searchText = "";


      const statusQuery = statusValue ? `status=${statusValue}` : "";
      const keywordQuery = searchText ? `keyword=${searchText}` : "";
      const queryParams = [statusQuery, keywordQuery].filter(Boolean).join("&");

      const url = `${process.env.REACT_APP_API_BASE_URL
        }/api/v1/userRegistration/registerUser${queryParams ? `?${queryParams}` : ""
        }`;
      const response = await axios.get(url);

      if (response.data.status == "success") {

        setUsers(response.data.data);
      }
    } catch (error) {

      console.error(
        "Error fetching user data:",
        error.response?.data || error.message
      );
    }


  };
 
 
const fetchAbstracts = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/Abstract/abstract`);
        setAbstracts(response.data);
      } catch (err) {
        console.error('Failed to load abstracts. Please try again.');
      }
    };
  

  return (
    <>
      <div className="dash">

        {/* ── Header ── */}
        <header className="dash-hdr">
          <div>
            <h1 className="dash-title">Dashboard</h1>
            <p className="dash-sub">Skinola International Aesthetics — Admin Panel</p>
          </div>
          <div className="dash-date">
            <span className="dash-dot" />
            {date}
          </div>
        </header>

        {/* ═══ ROW 1 — KPI STATS ═══ */}
        <div className="kpi-grid">
          {statsLoading
            ? Array.from({ length: 8 }).map((_, i) => <KpiSkeleton key={i} />)
            : statsError
              ? <div style={{ gridColumn: "1/-1", textAlign: "center", color: "#dc2626", padding: "24px", fontSize: 13, fontWeight: 600 }}>{statsError}</div>
              : stats.map((s, i) => (
                <div className="kpi" key={i}
                  style={{ "--kc": s.c, "--kb": s.bg, "--kbo": s.border, animationDelay: `${i * 0.04}s` }}>
                  <div className="kpi-top">
                    <div className="kpi-ico">{s.icon}</div>
                    <span className="kpi-delta"
                      style={{
                        color: s.up === null ? "#b45309" : s.up ? "#16a34a" : "#dc2626",
                        background: s.up === null ? "#fffbeb" : s.up ? "#f0fdf4" : "#fef2f2",
                      }}>
                      {s.up === true && <FaArrowUp size={8} />}
                      {s.up === false && <FaArrowDown size={8} />}
                      {" "}{s.delta}
                    </span>
                  </div>
                  <div className="kpi-val">{s.val}</div>
                  <div className="kpi-lbl">{s.label}</div>
                  <div className="kpi-stripe" />
                </div>
              ))
          }
        </div>

        {/* ═══ ROW 2 — COUNTDOWN + TODAY + EVENTS ═══ */}
        <div className="r3">

          {/* Countdown */}
          <div className="cd-card">
            <div className="cd-noise" />
            <div className="cd-eyebrow">
              <FaCalendarCheck size={12} /> Event Countdown
              {countdownEvent?.status === "live" && (
                <span className="cd-live">● LIVE</span>
              )}
            </div>
            <p className="cd-event-name">
              {countdownEvent?.title || "No Upcoming Event"}
            </p>
            <div className="cd-units">
              {[{ l: "Days", v: cd.d }, { l: "Hours", v: cd.h }, { l: "Mins", v: cd.m }, { l: "Secs", v: cd.s }].map((u, i) => (
                <React.Fragment key={i}>
                  <div className="cd-unit">
                    <div className="cd-num">{u.v}</div>
                    <div className="cd-lbl">{u.l}</div>
                  </div>
                  {i < 3 && <div className="cd-sep">:</div>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Today */}
          <div className="sec today-sec">
            <div className="sec-head">
              <div className="sec-head-left">
                <span className="sec-icon" style={{ background: `${V}12`, color: V, borderColor: `${V}22` }}>
                  <FaTicketAlt size={15} />
                </span>
                <span className="sec-label">Today's Registrations</span>
              </div>
            </div>
            <div className="today-body">
              <div className="today-main">
                <div>
                  <div className="today-num">{todayRegs?.todayCount}</div>
                  <div className="today-sub">tickets today</div>
                </div>
                <div className="today-badge">
                  <FaArrowUp size={10} /> {todayRegs?.deltaNum || 0}% vs yesterday
                </div>
              </div>
              <div className="cap-row">
                <span className="cap-label">Venue capacity</span>
                <span className="cap-val">{todayRegs?.capacityPct || 0} <span>/{todayRegs?.venueCapacity || 0}</span></span>
              </div>
              <div className="cap-bar-bg">
                <div className="cap-bar-fill" style={{ width: `${todayRegs?.capacityPct || 0}%` }} />
              </div>
              <div className="today-grid">
                {[{ l: "Confirmed", v: todayRegs?.confirmed || 0, c: "#16a34a" }, { l: "Pending", v: todayRegs?.pending || 0, c: "#b45309" }, { l: "Waitlisted", v: todayRegs?.waitlisted || 0, c: "#ea580c" }].map((x, i) => (
                  <div key={i} className="today-mini" style={{ "--mc": x.c }}>
                    <span className="tm-val">{x.v}</span>
                    <span className="tm-lbl">{x.l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Events */}
          <Section label="Upcoming Events" icon={<FaCalendarCheck size={15} />} iconColor="#ea580c" action="/event">
            <div className="ev-list">
              {events?.slice(0, 5).map((ev, i) => (
                <div key={i} className="ev-row">
                  <div className="ev-date-box" style={{ "--ec": ev.c }}>
                    <span className="ev-day">{ev.day}</span>
                    <span className="ev-wd">{ev.wd}</span>
                  </div>
                  <div className="ev-info">
                    <div className="ev-title">{ev.title}</div>
                    <div className="ev-meta">
                      <span className="ev-type" style={{ color: ev.c, background: `${ev.c}10`, borderColor: `${ev.c}22` }}>{ev.type}</span>
                      <div className="ev-progress-wrap">
                        <div className="ev-progress-bar" style={{ width: `${(ev.sold / ev.total || 0) * 100}%`, background: ev.c }} />
                      </div>
                      <span className="ev-count">{ev.sold}/{ev.total || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

        </div>

        {/* ═══ ROW 3 — CHARTS ═══ */}
        <div className="r2">
          <Section label="Registration Growth" icon={<FaUsers size={15} />} iconColor={P}>
            <div className="chart-wrap">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={regData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={P} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={P} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e8f8" vertical={false} />
                  <XAxis dataKey="d" tick={{ fill: "#a08ac0", fontSize: 10.5, fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#a08ac0", fontSize: 10.5 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #f0d8ef", borderRadius: 10, boxShadow: "0 8px 24px rgba(26,0,64,0.09)", fontSize: 12, fontWeight: 700 }} itemStyle={{ color: P }} cursor={{ stroke: "#f0d0e8", strokeWidth: 1.5 }} />
                  <Area type="monotone" dataKey="v" stroke={P} strokeWidth={2.5} fill="url(#ag)" dot={{ fill: P, r: 3.5, strokeWidth: 0 }} activeDot={{ r: 5, fill: P }} name="Registrations" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <Section label="Revenue by Month" icon={<FaRupeeSign size={15} />} iconColor="#16a34a">
            <div className="chart-wrap">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={revData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barSize={32}>
                  <defs>
                    <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={V} />
                      <stop offset="100%" stopColor={P} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e8f8" vertical={false} />
                  <XAxis dataKey="m" tick={{ fill: "#a08ac0", fontSize: 10.5, fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#a08ac0", fontSize: 10.5 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e8d8f8", borderRadius: 10, boxShadow: "0 8px 24px rgba(26,0,64,0.09)", fontSize: 12, fontWeight: 700 }} formatter={v => [`₹${v.toLocaleString("en-IN")}`, "Revenue"]} cursor={{ fill: "rgba(139,47,201,0.04)" }} />
                  <Bar dataKey="r" fill="url(#bg)" radius={[6, 6, 0, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>



        {/* ═══ ROW 5 — REGISTERED USERS TABLE ═══ */}
        <Section label="Registered Users" icon={<FaUsers size={15} />} iconColor={P} action="/registeruser">
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>{["Name & Email", "Phone", "Category", "Date", "Status"].map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {users?.slice(0, 6).map((u, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                        <Av name={u.user.name} />
                        <div>
                          <div className="tbl-name">{u.user.name}</div>
                          <div className="tbl-sub">{u.user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="tbl-muted">{u.user.mobile}</td>
                    <td><span className="tbl-cat">{u.selectedSessions[0].conferenceType}</span></td>
                    <td className="tbl-muted">
                      {new Date(u.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </td>
                    <td><Badge s={u.payment.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* ═══ ROW 6 — LEADS + DOWNLOADS ═══ */}
        <div className="r2">
          <Section label="Recent Leads" icon={<FaBullhorn size={15} />} iconColor="#ea580c" action="/GetInTouch">
            <div className="feed">
              {sortedData?.slice(0, 6).map((l, i) => (
                <div key={i} className="feed-row">
                  <Av name={l.name} size={38} />
                  <div className="feed-body">
                    <div className="feed-name">{l.name}</div>
                    <div className="feed-sub">{l.email}</div>

                  </div>
                  <span className="feed-time">
                    {formatDate(l.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          </Section>

          <Section label="Schedule PDF Downloads" icon={<FaFileDownload size={15} />} iconColor="#0284c7" action="/admin/downloads">
            <div className="feed">
              {PDFS.map((p, i) => (
                <div key={i} className="feed-row">
                  <Av name={p.name} size={38} />
                  <div className="feed-body">
                    <div className="feed-name">{p.name}</div>
                    <div className="feed-sub">{p.email}</div>
                    <span className="interest-chip" style={{ color: "#0284c7", background: "#f0f9ff", borderColor: "#bae6fd", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <FaFileDownload size={9} />{p.file}
                    </span>
                  </div>
                  <span className="feed-time">{p.time}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* ═══ ROW 7 — FEEDBACKS + ABSTRACTS ═══ */}
        <div className="r2">
          <Section label="Recent Feedbacks" icon={<FaCommentDots size={15} />} iconColor="#0f766e" action="/feedback">
            <div className="feed">
              {feedbacks?.slice(0, 5).map((f, i) => (
                <div key={i} className="feedback-row">
                  <div className="fb-header">
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <Av name={f.name} size={36} />
                      <div>
                        <div className="feed-name">{f.name}</div>
                        <Stars n={f.rating} />
                      </div>
                    </div>
                    <span className="feed-time">{formatDate(f.createdAt)}</span>
                  </div>
                  {f.feedbackText && (
                    <blockquote className="fb-quote">"{f.feedbackText}"</blockquote>
                  ) }
                </div>
              ))}
            </div>
          </Section>

          <Section label="Submitted Abstracts" icon={<FaFlask size={15} />} iconColor="#7c3aed" action="/Abstractlist" show={abstracts.length > 0}>
           {abstracts.length > 0 && (
                <div className="feed">
              {abstracts?.map((a, i) => (
                <div key={i} className="feed-row">
                  <div className="abs-num">{String(i + 1).padStart(2, "0")}</div>
                  <div className="feed-body">
                    <div className="feed-name" style={{ whiteSpace: "normal", lineHeight: 1.35 }}>{a.title}</div>
                    <div className="feed-sub">{a.name} · <span style={{ color: "#7c3aed" }}>{a.category}</span></div>
                  </div>
                  {a?.status &&  <Badge s={a?.status} />}
                </div>
              ))}
            </div>) || <div style={{ padding: "24px", textAlign: "center", color: "#6a5070", fontSize: 13, fontWeight: 500 }}>No abstracts submitted yet.</div>}
          </Section>
        </div>

      </div>

      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:0.35} }
        @keyframes shimmer{ 0%{background-position:-400px 0} 100%{background-position:400px 0} }

        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

        .dash {
          background:${BG};min-height:100vh;padding:32px 32px 80px;
          display:flex;flex-direction:column;gap:28px;font-family:inherit;
        }
        .dash-hdr { display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;animation:fadeUp .4s ease both; }
        .dash-title { font-size:26px;font-weight:800;color:${D};letter-spacing:-.025em; }
        .dash-sub   { font-size:13px;color:#9a86b8;margin-top:3px;font-weight:500; }
        .dash-date  { display:inline-flex;align-items:center;gap:8px;background:#fff;border:1px solid #e8d8f8;border-radius:50px;padding:9px 20px;font-size:12.5px;font-weight:600;color:#6a5070;box-shadow:0 2px 12px rgba(26,0,64,.05); }
        .dash-dot   { width:7px;height:7px;border-radius:50%;background:${P};animation:pulse 2s infinite;flex-shrink:0; }

        .kpi-grid { display:grid;grid-template-columns:repeat(4,1fr);gap:16px;animation:fadeUp .4s ease .05s both; }
        .kpi { background:#fff;border-radius:18px;border:1.5px solid var(--kbo);padding:22px 20px 18px;display:flex;flex-direction:column;position:relative;overflow:hidden;transition:transform .3s cubic-bezier(.16,1,.3,1),box-shadow .3s;animation:fadeUp .45s ease both;box-shadow:0 2px 12px rgba(26,0,64,.05); }
        .kpi:hover { transform:translateY(-5px);box-shadow:0 14px 32px rgba(26,0,64,.1); }
        .kpi-top  { display:flex;align-items:center;justify-content:space-between;margin-bottom:16px; }
        .kpi-ico  { width:42px;height:42px;border-radius:11px;background:var(--kb);color:var(--kc);display:flex;align-items:center;justify-content:center;transition:background .3s,border-radius .3s; }
        .kpi:hover .kpi-ico { background:var(--kc);color:#fff;border-radius:50%; }
        .kpi-delta { font-size:11px;font-weight:700;letter-spacing:.04em;padding:3px 9px;border-radius:6px;display:inline-flex;align-items:center;gap:3px; }
        .kpi-val  { font-size:26px;font-weight:800;color:${D};line-height:1;letter-spacing:-.03em;margin-bottom:5px; }
        .kpi-lbl  { font-size:11.5px;font-weight:600;color:#9a86b8;text-transform:uppercase;letter-spacing:.06em; }
        .kpi-stripe { position:absolute;bottom:0;left:0;right:0;height:3px;background:var(--kc);transform:scaleX(0);transform-origin:left;transition:transform .35s cubic-bezier(.16,1,.3,1);border-radius:0 0 18px 18px; }
        .kpi:hover .kpi-stripe { transform:scaleX(1); }

        .r2 { display:grid;grid-template-columns:1fr 1fr;gap:24px; }
        .r3 { display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px; }

        .cd-card { background:${D};border-radius:22px;border:1px solid rgba(240,77,160,.25);box-shadow:0 16px 48px rgba(26,0,64,.28);padding:28px 28px 32px;display:flex;flex-direction:column;position:relative;overflow:hidden;animation:fadeUp .45s ease both; }
        .cd-noise { position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse 180% 80% at 110% 0%,rgba(240,77,160,.18),transparent 60%),radial-gradient(ellipse 150% 80% at -20% 110%,rgba(139,47,201,.2),transparent 60%); }
        .cd-eyebrow { display:inline-flex;align-items:center;gap:6px;font-size:10.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(255,255,255,.45);margin-bottom:8px;position:relative;z-index:1; }
        .cd-live { color:${P};margin-left:4px;font-size:10px;letter-spacing:.1em;animation:pulse 2s infinite; }
        .cd-event-name { font-size:13.5px;font-weight:700;color:rgba(255,255,255,.8);margin-bottom:28px;line-height:1.4;position:relative;z-index:1; }
        .cd-units { display:flex;align-items:center;justify-content:center;gap:0;position:relative;z-index:1; }
        .cd-unit  { display:flex;flex-direction:column;align-items:center;gap:6px;flex:1; }
        .cd-num   { font-size:clamp(30px,3vw,48px);font-weight:800;line-height:1;letter-spacing:-.04em;background:linear-gradient(135deg,#fff,rgba(255,255,255,.7));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text; }
        .cd-lbl   { font-size:10px;font-weight:700;color:rgba(255,255,255,.35);text-transform:uppercase;letter-spacing:.12em; }
        .cd-sep   { font-size:32px;font-weight:800;color:${P};opacity:.65;padding-bottom:18px;margin:0 2px; }

        .today-sec  { animation:fadeUp .45s ease both; }
        .today-body { padding:4px 22px 24px; }
        .today-main { display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:20px; }
        .today-num  { font-size:54px;font-weight:800;color:${D};line-height:1;letter-spacing:-.04em; }
        .today-sub  { font-size:11px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.07em;margin-top:5px; }
        .today-badge { display:inline-flex;align-items:center;gap:4px;font-size:12.5px;font-weight:700;color:#16a34a;background:#f0fdf4;border:1px solid #bbf7d0;padding:6px 12px;border-radius:8px;margin-bottom:6px; }
        .cap-row  { display:flex;justify-content:space-between;margin-bottom:7px; }
        .cap-label{ font-size:11px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.06em; }
        .cap-val  { font-size:11px;font-weight:800;color:${V}; }
        .cap-val span { color:#9a86b8;font-weight:500; }
        .cap-bar-bg   { height:8px;border-radius:8px;background:#f0e8f8;overflow:hidden;margin-bottom:18px; }
        .cap-bar-fill { height:100%;border-radius:8px;background:${G};transition:width 1s cubic-bezier(.16,1,.3,1); }
        .today-grid { display:grid;grid-template-columns:repeat(3,1fr);gap:10px; }
        .today-mini { background:color-mix(in srgb,var(--mc) 8%,transparent);border:1px solid color-mix(in srgb,var(--mc) 22%,transparent);border-radius:12px;padding:12px 8px;text-align:center; }
        .tm-val { display:block;font-size:20px;font-weight:800;color:var(--mc);line-height:1; }
        .tm-lbl { display:block;font-size:10px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.05em;margin-top:4px; }

        .ev-list { display:flex;flex-direction:column; }
        .ev-row  { display:flex;align-items:center;gap:14px;padding:13px 20px;border-top:1px solid #f3ecfa;transition:background .2s;cursor:pointer; }
        .ev-row:first-child { border-top:none; }
        .ev-row:hover { background:#fdf8ff; }
        .ev-date-box { width:46px;height:52px;border-radius:12px;flex-shrink:0;background:color-mix(in srgb,var(--ec) 10%,transparent);border:1px solid color-mix(in srgb,var(--ec) 20%,transparent);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px; }
        .ev-day  { font-size:18px;font-weight:800;color:var(--ec);line-height:1; }
        .ev-wd   { font-size:9px;font-weight:700;color:var(--ec);opacity:.7;text-transform:uppercase;letter-spacing:.06em; }
        .ev-info { flex:1;min-width:0; }
        .ev-title{ font-size:13px;font-weight:700;color:${D};margin-bottom:5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
        .ev-meta { display:flex;align-items:center;gap:8px; }
        .ev-type { font-size:10px;font-weight:700;padding:2px 8px;border-radius:5px;border:1px solid;white-space:nowrap;text-transform:uppercase;letter-spacing:.04em; }
        .ev-progress-wrap { flex:1;height:5px;border-radius:4px;background:#f0e8f8;overflow:hidden; }
        .ev-progress-bar  { height:100%;border-radius:4px;transition:width .8s ease; }
        .ev-count { font-size:10px;font-weight:700;color:#9a86b8;white-space:nowrap; }

        .sec { background:#fff;border-radius:20px;border:1px solid #ede5f5;box-shadow:0 2px 16px rgba(26,0,64,.05);overflow:hidden;animation:fadeUp .45s ease both; }
        .sec-head { display:flex;align-items:center;justify-content:space-between;padding:18px 22px 16px;border-bottom:1px solid #f3ecfa; }
        .sec-head-left { display:flex;align-items:center;gap:12px; }
        .sec-icon  { width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;border:1px solid;flex-shrink:0; }
        .sec-label { font-size:14.5px;font-weight:800;color:${D};letter-spacing:-.01em; }
        .sec-action { display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:700;color:${P};text-decoration:none;padding:5px 13px;border-radius:7px;background:#fff0f8;border:1px solid #ffd0ea;transition:background .2s,transform .2s;white-space:nowrap; }
        .sec-action:hover { background:#ffe0f2;transform:translateX(2px); }

        .chart-wrap { padding:12px 16px 16px; }

        .donut-wrap   { display:flex;align-items:center;gap:20px;padding:12px 20px 20px; }
        .donut-legend { display:flex;flex-direction:column;gap:9px;flex:1; }
        .legend-row   { display:flex;align-items:center;gap:8px; }
        .legend-dot   { width:9px;height:9px;border-radius:50%;flex-shrink:0; }
        .legend-name  { font-size:12.5px;font-weight:500;color:#5a5070;flex:1; }
        .legend-val   { font-size:13px;font-weight:800;color:${D}; }

        .breakdown-wrap { padding:12px 22px 20px;display:flex;flex-direction:column;gap:20px; }
        .breakdown-bars { display:flex;flex-direction:column;gap:16px; }
        .bbar-top  { display:flex;justify-content:space-between;margin-bottom:7px; }
        .bbar-label{ font-size:13px;font-weight:600;color:#5a5070; }
        .bbar-count{ font-size:13px;font-weight:800;color:${D}; }
        .bbar-count em { font-style:normal;font-size:11px;font-weight:500;color:#9a86b8;margin-left:3px; }
        .bbar-bg   { height:9px;border-radius:8px;background:#f0e8f8;overflow:hidden; }
        .bbar-fill { height:100%;border-radius:8px;transition:width 1s cubic-bezier(.16,1,.3,1); }
        .mini-kpis { display:grid;grid-template-columns:1fr 1fr;gap:10px; }
        .mini-kpi  { background:color-mix(in srgb,var(--mk) 7%,transparent);border:1px solid color-mix(in srgb,var(--mk) 20%,transparent);border-radius:12px;padding:13px 14px; }
        .mk-val { display:block;font-size:22px;font-weight:800;color:var(--mk);line-height:1; }
        .mk-lbl { display:block;font-size:10px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.06em;margin-top:4px; }

        .tbl { width:100%;border-collapse:collapse;font-size:13px; }
        .tbl thead tr { background:#faf6fe; }
        .tbl th { padding:13px 18px;text-align:left;font-size:10.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#8a76a8;white-space:nowrap;border-bottom:1px solid #f0e8f8; }
        .tbl td { padding:14px 18px;border-top:1px solid #f5f0fa;vertical-align:middle; }
        .tbl tbody tr:hover td { background:#fdf8ff; }
        .tbl-name { font-size:13.5px;font-weight:700;color:${D}; }
        .tbl-sub  { font-size:11.5px;color:#9a86b8;margin-top:2px; }
        .tbl-muted{ color:#8a76a8;font-size:13px; }
        .tbl-cat  { font-size:11px;font-weight:700;color:${V};background:#f5f0ff;border:1px solid #e9d8fc;padding:3px 10px;border-radius:6px;white-space:nowrap; }

        .feed { display:flex;flex-direction:column; }
        .feed-row { display:flex;align-items:flex-start;gap:12px;padding:15px 22px;border-top:1px solid #f3ecfa;transition:background .15s; }
        .feed-row:first-child { border-top:none; }
        .feed-row:hover { background:#fdf8ff; }
        .feed-body { flex:1;min-width:0; }
        .feed-name { font-size:13.5px;font-weight:700;color:${D};margin-bottom:2px; }
        .feed-sub  { font-size:12px;color:#8a76a8;margin-bottom:4px; }
        .feed-time { font-size:11px;color:#b4a4c8;font-weight:600;white-space:nowrap;flex-shrink:0;margin-top:2px; }
        .interest-chip { display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:700;padding:3px 10px;border-radius:6px;border:1px solid;letter-spacing:.03em; }

        .feedback-row { padding:16px 22px;border-top:1px solid #f3ecfa;transition:background .15s; }
        .feedback-row:first-child { border-top:none; }
        .feedback-row:hover { background:#fdf8ff; }
        .fb-header { display:flex;align-items:center;justify-content:space-between;margin-bottom:10px; }
        .fb-quote  { font-size:12.5px;color:#6a5a7a;line-height:1.7;font-style:italic;background:#fdf8ff;border-left:3px solid rgba(240,77,160,.35);border-radius:0 8px 8px 0;padding:9px 14px;margin:0; }

        .abs-num { width:32px;height:32px;border-radius:8px;flex-shrink:0;background:#f5f0ff;border:1px solid #e9d8fc;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;color:#7c3aed;margin-top:3px; }

        @media(max-width:1280px){ .kpi-grid{grid-template-columns:repeat(4,1fr)} .r3{grid-template-columns:1fr 1fr} .r3>:last-child{grid-column:span 2} }
        @media(max-width:900px) { .kpi-grid{grid-template-columns:repeat(2,1fr)} .r2,.r3{grid-template-columns:1fr} .r3>:last-child{grid-column:span 1} }
        @media(max-width:600px) { .dash{padding:14px 12px 52px} .kpi-val{font-size:18px} .dash-date{display:none} .donut-wrap{flex-direction:column} .tbl th:nth-child(2),.tbl td:nth-child(2){display:none} }
      `}</style>
    </>
  );
}
